<script setup lang="ts">
import { VCol, VRow, VTooltip, VIcon } from 'vuetify/lib';

/*VUE2*/ import VBtn from '@mimir/web/components/V3Btn.vue';
import { ref, onMounted  } from 'vue';
import type { ComponentPublicInstance } from 'vue';

const actionButtonRef = ref<ComponentPublicInstance | null>(null);

const props = defineProps({
  actionText: { type: String, default: 'Ok' },
  secondaryActionText: { type: String, default: '' },
  cancelText: { type: String, default: 'Cancel' },
  disableAction: { type: Boolean, default: false },
  disableSecondaryAction: { type: Boolean, default: false },
  disableCancel: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  infoOnly: { type: Boolean, default: false },
  disabledTooltip: { type: String, default: '' },
  disabledCancelTooltip: { type: String, default: '' },
  hideActionButton: { type: Boolean, default: false },
  hideCancelButton: { type: Boolean, default: false },
  actionButtonIcon: { type: String, default: '' },
  shouldFocusActionButton: { type: Boolean, default: false },
  triggerActionOnMouseDownInsteadOfClick: { type: Boolean, default: false },
});

const focusActionButton = () => {
  if (actionButtonRef.value?.$el) {
    setTimeout(() => {
      const button = actionButtonRef.value?.$el as HTMLButtonElement | undefined;
      button?.focus();
    }, 100);
  }
};

onMounted(() => {
  if (props.shouldFocusActionButton) {
    focusActionButton();
  }
});

</script>

<template>
  <VRow
    justify="center"
    dense
  >
    <VCol
      v-if="!hideActionButton"
      cols="auto"
    >
      <VTooltip
        bottom
        :disabled="!disabledTooltip"
      >
        <template #activator="{ on }">
          <span v-on="on">
            <VBtn
              ref="actionButtonRef"
              size="small"
              variant="outlined"
              class="text-none rounded emphasis"
              :loading="loading"
              :disabled="disableAction"
              aria-label="action"
              @click="triggerActionOnMouseDownInsteadOfClick ? null : $emit('actionClicked', $event)"
              @mousedown="triggerActionOnMouseDownInsteadOfClick ? $emit('actionClicked', $event) : null"
            >
              <span
                v-if="actionButtonIcon"
                class="mr-1"
              > <VIcon color="secondary"> {{ actionButtonIcon }} &nbsp;&nbsp;</VIcon> </span>{{ actionText }}
            </VBtn>
          </span>
        </template>
        <span v-html="disabledTooltip" />
      </VTooltip>
    </VCol>
    <VCol
      v-if="secondaryActionText"
      cols="auto"
    >
      <VBtn
        size="small"
        variant="outlined"
        class="text-none rounded cancel-button"
        :disabled="disableSecondaryAction"
        aria-label="action2"
        @click="$emit('secondaryActionClicked', $event)"
      >
        {{ secondaryActionText }}
      </VBtn>
    </VCol>
    <VCol
      v-if="!hideCancelButton"
      cols="auto"
    >
      <VTooltip
        bottom
        :disabled="!disableCancel || !disabledCancelTooltip"
      >
        <template #activator="{ on }">
          <span v-on="on">
            <VBtn
              v-if="!infoOnly"
              size="small"
              variant="outlined"
              class="text-none rounded cancel-button"
              :disabled="disableCancel"
              aria-label="cancel"
              @click="$emit('cancelClicked')"
            >
              {{ cancelText }}
            </VBtn>
          </span>
        </template>
        {{ disabledCancelTooltip }}
      </VTooltip>
    </VCol>
  </VRow>
</template>
